import type { FacilityAction } from "@bw/shared/types";

import {
  COURT_SETUP_ALLOWED_STATUSES,
  STAFF_SETUP_ALLOWED_STATUSES,
  UNLOCKED_ORG_STATUSES,
} from "@/app/org-access-policy";

export type NavOrgAccess =
  | "approved"
  | "staff-setup"
  | "court-setup"
  | "authenticated";

export interface NavAccessPolicy {
  /** Client-side visibility hints; the server remains authoritative. */
  requiredAction?: FacilityAction;
  requiredAnyActions?: readonly FacilityAction[];
  /** Exact org-wide permission projected by the selected workspace endpoint. */
  requiresStaffManagement?: boolean;
  /** Fresh canonical org finance READ hint; never a facility-role inference. */
  requiresFinanceRead?: boolean;
  requiresTeamAccess?: boolean;
  requiresFinanceManagement?: boolean;
  /** Organization-owner surface. The server remains authoritative on access. */
  requiresOrganizationOwner?: boolean;
  /** Organization-status policy. Omitted items require an approved org. */
  orgAccess?: NavOrgAccess;
}

export interface NavAccessContext {
  scopeToActions: boolean;
  actions: readonly string[];
  /** Missing/loading hints fail closed; never infer this from facility grants. */
  canManageStaff?: boolean;
  financeRead?: boolean;
  canManageFinance?: boolean;
  /** Exact ownership of the selected organization. Missing/loading fails closed. */
  organizationOwner?: boolean;
}

/**
 * Pure organization-status gate shared by every navigation surface. Unknown,
 * loading, and terminal states fail closed except for authenticated routes.
 */
export function isNavItemAvailableForOrg(
  item: NavAccessPolicy,
  status: string | null | undefined,
): boolean {
  const access = item.orgAccess ?? "approved";
  if (access === "authenticated") return true;
  if (!status) return false;
  if (access === "staff-setup") {
    return STAFF_SETUP_ALLOWED_STATUSES.has(status);
  }
  if (access === "court-setup") return COURT_SETUP_ALLOWED_STATUSES.has(status);
  return UNLOCKED_ORG_STATUSES.has(status);
}

export function isNavItemVisibleForActions(
  item: NavAccessPolicy,
  opts: NavAccessContext,
): boolean {
  if (item.requiresOrganizationOwner && opts.organizationOwner !== true)
    return false;
  if (item.requiresStaffManagement && opts.canManageStaff !== true)
    return false;
  if (item.requiresFinanceRead && opts.financeRead !== true) return false;
  if (item.requiresFinanceManagement && opts.canManageFinance !== true)
    return false;
  if (
    item.requiresTeamAccess &&
    opts.canManageStaff !== true &&
    opts.canManageFinance !== true
  )
    return false;
  if (!opts.scopeToActions) return true;
  if (item.requiredAnyActions?.length) {
    return item.requiredAnyActions.some((action) =>
      opts.actions.includes(action),
    );
  }
  if (item.requiredAction) return opts.actions.includes(item.requiredAction);
  return true;
}
